import { useCallback } from 'react'
import { TipoReceitaSncr } from '_metronic/helpers'
import { NecessidadesSncr, useSncr, useSncrContexto } from '../../../sncr/hooks/useSncr'
import { saldoDoTipo } from '../../../sncr/core/_models'

export type LinhaSaldoSncr = {
  tipo: TipoReceitaSncr
  necessario: number
  disponivel: number
  falta: number
  /** Abaixo do limiar de alerta do backend, mesmo que dê para estes documentos. */
  baixo: boolean
  /** Sem número suficiente e a empresa exige numeração: a assinatura fica bloqueada. */
  bloqueia: boolean
}

/**
 * Saldo SNCR de cada tipo que os documentos vão consumir. Os números são reservados ao
 * finalizar (antes da assinatura), então quem exige numeração precisa de saldo antes de assinar.
 */
export const useSaldoSncr = (idEmpresa: number | undefined, necessidades: NecessidadesSncr) => {
  const tipos = (Object.keys(necessidades) as TipoReceitaSncr[]).filter(
    (tipo) => (necessidades[tipo] ?? 0) > 0
  )
  const { data: contexto, refetch } = useSncrContexto(tipos.length > 0 ? idEmpresa : undefined)
  const { showRetiradaModal } = useSncr()

  const habilitado = !!contexto?.habilitado
  const linhas: LinhaSaldoSncr[] = habilitado
    ? tipos.map((tipo) => {
        const saldo = saldoDoTipo(contexto, tipo)
        const necessario = necessidades[tipo] ?? 0
        const disponivel = saldo?.saldo ?? 0
        const falta = Math.max(0, necessario - disponivel)
        return {
          tipo,
          necessario,
          disponivel,
          falta,
          baixo: falta === 0 && !!saldo?.alerta,
          bloqueia: falta > 0 && !!contexto?.exigirNumeracao,
        }
      })
    : []

  const retirar = useCallback(
    async (tipo: TipoReceitaSncr) => {
      if (!idEmpresa || !contexto) return
      const resultado = await showRetiradaModal(idEmpresa, tipo, contexto)
      if (resultado) await refetch()
    },
    [idEmpresa, contexto, showRetiradaModal, refetch]
  )

  return { linhas, retirar }
}
